/**
 * The context dialog's five rows (BaglamNormal, BaglamDolu, D-120): what is sent with every message (the system
 * instruction, the tools, the skills, the environment), then the conversation. They always add up to the total
 * the header shows and the bar draws: the fixed parts keep their estimates, the system instruction carrying the
 * project's and the person's own instructions and the memory, and "Mesajlar" is what is left of the measured
 * total (with the documents read into it). Estimated above the total, the fixed parts are scaled down to it.
 */
export const CONTEXT_ROWS = ['system', 'tools', 'skills', 'environment', 'messages'] as const;
export type ContextRowKey = (typeof CONTEXT_ROWS)[number];

/** what the system instruction row carries besides Oppy's own instructions, when there is any */
export const SYSTEM_EXTRAS = ['project', 'instructions', 'memory'] as const;

export interface ContextRows {
  total: number;
  rows: { key: ContextRowKey; tokens: number }[];
  /** the extras in the system instruction row */
  extras: (typeof SYSTEM_EXTRAS)[number][];
}

export function contextRows(total: number, categories: readonly { key: string; tokens: number }[]): ContextRows {
  const of = (key: string) => Math.max(0, categories.filter((c) => c.key === key).reduce((sum, c) => sum + c.tokens, 0));
  const target = Math.max(0, Math.round(total));
  const extras = SYSTEM_EXTRAS.filter((key) => of(key) > 0);
  const fixed = [
    { key: 'system' as const, tokens: of('system') + extras.reduce((sum, key) => sum + of(key), 0) },
    { key: 'tools' as const, tokens: of('tools') },
    { key: 'skills' as const, tokens: of('skills') },
    { key: 'environment' as const, tokens: of('environment') },
  ];
  const fixedSum = fixed.reduce((sum, row) => sum + row.tokens, 0);
  if (fixedSum <= target) return { total: target, rows: [...fixed, { key: 'messages', tokens: target - fixedSum }], extras };
  // the fixed parts alone over the total: shares of it, the remainders given out largest first so the sum holds
  const exact = fixed.map((row) => (row.tokens / fixedSum) * target);
  const floors = exact.map(Math.floor);
  let left = target - floors.reduce((sum, value) => sum + value, 0);
  const order = exact.map((value, index) => ({ index, part: value - Math.floor(value) })).sort((a, b) => b.part - a.part);
  for (const { index } of order) {
    if (left <= 0) break;
    floors[index] = (floors[index] ?? 0) + 1;
    left -= 1;
  }
  return { total: target, rows: [...fixed.map((row, index) => ({ key: row.key, tokens: floors[index] ?? 0 })), { key: 'messages', tokens: 0 }], extras };
}
